import { JSX } from 'react';
import {
    TextInput,
    ArrayInput,
    NumberInput,
    SimpleFormIterator,
    BooleanInput,
    SelectInput,
    required,
    FormDataConsumer, FormDataConsumerRenderParams
} from 'react-admin';
import {
    Alert,
    Box,
    Grid,
    Stack,
    Typography
} from '@mui/material';
import { FormSection } from "../../../FormSection";

const SearchTypes = [
    { id: 'NER', name: "NER" },
    { id: 'Regex', name: "Regex" },
    { id: 'Pattern', name: "Pattern" },
    { id: 'Socials', name: "Socials" },
];

const NerOptions = [
    {
        id: 'PERSON',
        name: 'PERSON',
    },
    {
        id: 'NORP',
        name: 'NORP',
    },
    {
        id: 'FAC',
        name: 'FAC',
    },
    {
        id: 'ORG',
        name: 'ORG',
    },
    {
        id: 'GPE',
        name: 'GPE',
    },
    {
        id: 'LOC',
        name: 'LOC',
    },
    {
        id: 'PRODUCT',
        name: 'PRODUCT',
    },
    {
        id: 'EVENT',
        name: 'EVENT',
    },
    {
        id: 'WORK_OF_ART',
        name: 'WORK OF ART',
    },
    {
        id: 'LAW',
        name: 'LAW',
    },
    {
        id: 'LANGUAGE',
        name: 'LANGUAGE',
    },
    {
        id: 'DATE',
        name: 'DATE',
    },
    {
        id: 'TIME',
        name: 'DATE',
    },
    {
        id: 'PERCENT',
        name: 'PERCENT',
    },
    {
        id: 'MONEY',
        name: 'MONEY',
    },
    {
        id: 'QUANTITY',
        name: 'QUANTITY',
    },
    {
        id: 'ORDINAL',
        name: 'ORDINAL',
    },
    {
        id: 'CARDINAL',
        name: 'CARDINAL',
    },
    {
        id: 'LOC_BOTH',
        name: 'LOC & GPE',
    }
]

/**
 * The three keyword lists hold one short value per row, so left as ordinary
 * iterator lines twenty of them become twenty full width rows and most of the
 * panel's height. Wrapping the list into columns keeps every value visible
 * without scrolling past it. The rows also lose their separator line, which
 * only ever divided one input from the next.
 */
const wrappedListSx = {
    '& .RaSimpleFormIterator-list': {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        columnGap: 2,
    },
    '& .RaSimpleFormIterator-line': {
        borderBottom: 'none',
    },
};

export const EmailFinderConfigurationPanel: () => JSX.Element = (): JSX.Element => {
    return (
        <Box sx={ { width: '100%' } }>
            <ArrayInput source="configuration" label={ false }>
                <SimpleFormIterator disableReordering
                                    disableRemove
                                    disableAdd
                                    sx={ { '& .RaSimpleFormIterator-line': { borderBottom: 'none' } } }>
                    <Stack spacing={ 4 } sx={ { width: '100%' } }>
                        <FormSection title="Scraper">
                            <Grid container spacing={ 2 }>
                                <Grid size={ { xs: 12, md: 6 } }>
                                    <TextInput source="user_agent"
                                               helperText="User agent for the browser. Leave blank for system default"
                                               fullWidth />
                                </Grid>
                                <Grid size={ { xs: 6, md: 3 } }>
                                    <NumberInput source="maximum_anchor_climb"
                                                 helperText="Maximum search anchor parent climb count"
                                                 fullWidth />
                                </Grid>
                                <Grid size={ { xs: 6, md: 3 } }>
                                    <NumberInput source="parallel_count"
                                                 helperText="Maximum number of parallel scrapes to run at a time"
                                                 fullWidth />
                                </Grid>
                            </Grid>
                        </FormSection>

                        <FormSection title="Search points">
                            <Alert severity="warning" sx={ { marginBottom: '16px' } }>
                                { "Search Point Names map to System Values, be careful renaming!" }
                            </Alert>
                            <ArrayInput source="search_points" label={ false }>
                                <SimpleFormIterator getItemLabel={ (index: number): string => String(index + 1) }
                                                    sx={ {
                                                        '& .RaSimpleFormIterator-line': {
                                                            borderBottom: 'none',
                                                            alignItems: 'flex-start',
                                                            padding: '16px 0 0 0',
                                                        },
                                                        '& .RaSimpleFormIterator-index': {
                                                            marginTop: '16px',
                                                        },
                                                    } }>
                                    <Box sx={ {
                                        width: '100%',
                                        padding: '16px',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                    } }>
                                        <Grid container spacing={ 2 }>
                                            <Grid size={ { xs: 12, md: 6 } }>
                                                <TextInput source="name"
                                                           helperText={ false }
                                                           validate={[required()]}
                                                           fullWidth />
                                            </Grid>
                                            <Grid size={ { xs: 12, md: 6 } }>
                                                <SelectInput source="search_type"
                                                             choices={ SearchTypes }
                                                             validate={[required()]}
                                                             fullWidth />
                                            </Grid>
                                            <Grid size={ { xs: 12, md: 6 } }>
                                                <TextInput source="description"
                                                           multiline
                                                           rows={4}
                                                           helperText={ false }
                                                           fullWidth />
                                            </Grid>
                                            <Grid size={ { xs: 12, md: 6 } }>
                                                <FormDataConsumer>
                                                    {({ scopedFormData }: FormDataConsumerRenderParams): JSX.Element =>
                                                        scopedFormData?.search_type === 'NER' ? (
                                                            <SelectInput source="ner_option"
                                                                         choices={ NerOptions }
                                                                         validate={[required()]}
                                                                         fullWidth />
                                                        ) : scopedFormData?.search_type === 'Regex' ? (
                                                            <TextInput source="regex_value"
                                                                       multiline
                                                                       rows={4}
                                                                       validate={[required()]}
                                                                       fullWidth />
                                                        ) : scopedFormData?.search_type === 'Pattern' ? (
                                                            <Stack spacing={ 2 }>
                                                                <TextInput source="keywords"
                                                                           multiline
                                                                           rows={4}
                                                                           fullWidth />
                                                                <TextInput source="entity_keywords"
                                                                           multiline
                                                                           rows={4}
                                                                           validate={[required()]}
                                                                           fullWidth />
                                                            </Stack>
                                                        ) : null
                                                    }
                                                </FormDataConsumer>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </SimpleFormIterator>
                            </ArrayInput>
                        </FormSection>

                        <FormSection title="Excluded URLs"
                                 hint="Values will be compared to a URL to exclude (do not include the https://)">
                            <ArrayInput source="exclude_urls" label={ false }>
                                <SimpleFormIterator inline sx={ wrappedListSx }>
                                    <TextInput source="url" helperText={ false } fullWidth />
                                </SimpleFormIterator>
                            </ArrayInput>
                        </FormSection>

                        <FormSection title="Filter keywords"
                                 hint="Values will be compared to a URL to exclude (do not include the https://) Values which might be present in website data filters to reduce scraping of category pages">
                            <ArrayInput source="filter_keys" label={ false }>
                                <SimpleFormIterator inline sx={ wrappedListSx }>
                                    <TextInput source="keyword" helperText={ false } fullWidth />
                                </SimpleFormIterator>
                            </ArrayInput>
                        </FormSection>

                        <FormSection title="eCommerce pages">
                            <BooleanInput source="exclude_ecommerce_pages"
                                          helperText="Exclude eCommerce Product and Category Pages" />
                            <ArrayInput source="exclude_ecommerce_hints" label="Hints">
                                <SimpleFormIterator inline sx={ wrappedListSx }>
                                    <TextInput source="hint" helperText={ false } fullWidth />
                                </SimpleFormIterator>
                            </ArrayInput>
                        </FormSection>
                    </Stack>
                </SimpleFormIterator>
            </ArrayInput>
        </Box>
    );
}
